import React from 'react';
import './InputSliders.css';
import { useMain } from '../../store/StoredContext';

interface InputSlidersProps {
  isBikeFactorValuesChanged: boolean;
  setIsBikeFactorValuesChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

interface SliderStyles extends React.CSSProperties {
  '--thumb-color': string;
}

const InputSliders = ({ isBikeFactorValuesChanged, setIsBikeFactorValuesChanged }: InputSlidersProps) => {
  const { state: { bikeFactor }, setBikeFactor } = useMain();

  const handleSliderChange = (factor: 'timeFactor' | 'slopeFactor' | 'safetyFactor', value: number) => {
    const normalizedValue = value / 100;
    const otherFactors = ['timeFactor', 'slopeFactor', 'safetyFactor'].filter(f => f !== factor) as Array<'timeFactor' | 'slopeFactor' | 'safetyFactor'>;
    const remaining = 1 - normalizedValue;
    const currentOtherSum = otherFactors.reduce((sum, f) => sum + bikeFactor[f], 0);
    const ratio = currentOtherSum > 0 ? remaining / currentOtherSum : 1;

    const newValues = {
      timeFactor: bikeFactor.timeFactor,
      slopeFactor: bikeFactor.slopeFactor,
      safetyFactor: bikeFactor.safetyFactor,
      isPersonalBike: bikeFactor.isPersonalBike
    };

    newValues[factor] = normalizedValue;
    otherFactors.forEach(f => {
      newValues[f] = currentOtherSum > 0
        ? bikeFactor[f] * ratio
        : remaining / otherFactors.length;
    });

    setBikeFactor(newValues);
    setIsBikeFactorValuesChanged(true);
  };

  const setPersonalBike = () => {
    setBikeFactor({
      ...bikeFactor,
      isPersonalBike: !bikeFactor.isPersonalBike
    });
  };

  const getSliderStyles = (value: number): SliderStyles => {
    const styles: SliderStyles = {
      width: '100%',
      height: '4px',
      WebkitAppearance: 'none',
      borderRadius: '2px',
      outline: 'none',
      '--thumb-color': '#1bd98d',
      background: `linear-gradient(to right, #1bd98d 0%, #1bd98d ${value}%, #e5e7eb ${value}%, #e5e7eb 100%)`,
      margin: '0',
      padding: '0'
    };
    return styles;
  };

  const sliderThumbClasses = [
    '[&::-webkit-slider-thumb]:appearance-none',
    '[&::-webkit-slider-thumb]:w-4',
    '[&::-webkit-slider-thumb]:h-4',
    '[&::-webkit-slider-thumb]:rounded-full',
    '[&::-webkit-slider-thumb]:bg-[var(--thumb-color)]',
    '[&::-webkit-slider-thumb]:cursor-pointer',
    '[&::-moz-range-thumb]:w-4',
    '[&::-moz-range-thumb]:h-4',
    '[&::-moz-range-thumb]:rounded-full',
    '[&::-moz-range-thumb]:bg-[var(--thumb-color)]',
    '[&::-moz-range-thumb]:border-0',
    '[&::-moz-range-thumb]:cursor-pointer'
  ].join(' ');

  return (
    <div className="triangle-container w-full max-w-3xl mx-auto px-8">
      <h2 className="triangle-title mb-8">Paramètres Vélo</h2>

      <div className="triangle-values w-full space-y-8">
        <div className="space-y-8 w-full">
          {/* Time Factor Slider */}
          <div className="w-full">
            <div className="flex mb-2">
              <label className="value-title text-lg">Rapide</label>
              <div className="flex-grow"></div>
              <span className="value-number text-lg text-[#1bd98d]">{Math.round(bikeFactor.timeFactor * 100)}%</span>
            </div>
            <div className="w-full">
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(bikeFactor.timeFactor * 100)}
                onChange={(e) => { handleSliderChange('timeFactor', parseInt(e.target.value)); }}
                style={getSliderStyles(Math.round(bikeFactor.timeFactor * 100))}
                className={`w-full ${sliderThumbClasses}`}
              />
            </div>
          </div>

          {/* Slope Factor Slider */}
          <div className="w-full">
            <div className="flex mb-2">
              <label className="value-title text-lg">Plat</label>
              <div className="flex-grow"></div>
              <span className="value-number text-lg text-[#1bd98d]">{Math.round(bikeFactor.slopeFactor * 100)}%</span>
            </div>
            <div className="w-full">
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(bikeFactor.slopeFactor * 100)}
                onChange={(e) => { handleSliderChange('slopeFactor', parseInt(e.target.value)); }}
                style={getSliderStyles(Math.round(bikeFactor.slopeFactor * 100))}
                className={`w-full ${sliderThumbClasses}`}
              />
            </div>
          </div>

          {/* Safety Factor Slider */}
          <div className="w-full">
            <div className="flex mb-2">
              <label className="value-title text-lg">Sûr</label>
              <div className="flex-grow"></div>
              <span className="value-number text-lg text-[#1bd98d]">{Math.round(bikeFactor.safetyFactor * 100)}%</span>
            </div>
            <div className="w-full">
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(bikeFactor.safetyFactor * 100)}
                onChange={(e) => { handleSliderChange('safetyFactor', parseInt(e.target.value)); }}
                style={getSliderStyles(Math.round(bikeFactor.safetyFactor * 100))}
                className={`w-full ${sliderThumbClasses}`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Toggle for Personal Bike */}
      <div className="flex items-center justify-center mt-8">
        <label className="toggle-label">
          <input
            type="checkbox"
            className="toggle-checkbox"
            checked={bikeFactor.isPersonalBike}
            onChange={setPersonalBike}
          />
          <span className="toggle-slider"></span>
        </label>
        <span className="toggle-text ml-2">
          {bikeFactor.isPersonalBike ? 'Vélo personnel' : 'Vélo en libre-service'}
        </span>
      </div>
    </div>
  );
};

export default InputSliders;
